import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-sm border-b border-background-200/70'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="font-heading text-lg md:text-xl font-semibold text-foreground-950 whitespace-nowrap cursor-pointer hover:text-primary-500 transition-colors"
            >
              Superbuy Sheet
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-5">
              <Link
                to="/guide"
                className="text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Complete Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </span>
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 cursor-pointer"
              aria-label="Toggle menu"
            >
              {mobileOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          ></div>
          <div className="absolute top-0 right-0 w-[85%] max-w-sm h-full bg-background-50 shadow-lg overflow-y-auto">
            <div className="p-5 pt-20">
              {/* Quick CTAs */}
              <div className="flex flex-col gap-2 mb-6">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-5 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Superbuy Sheet
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center gap-2 px-5 py-3 bg-background-100 text-foreground-800 font-medium text-sm rounded-full border border-background-300/60 hover:bg-background-200 transition-colors cursor-pointer"
                >
                  Buy It Now
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-xs"></i>
                  </span>
                </a>
              </div>

              {/* Guide Links */}
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">
                  SEO Guides
                </h4>
                <div className="space-y-0.5">
                  <Link
                    to="/guide"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-primary-500">
                      <i className="ri-book-open-line text-sm"></i>
                    </span>
                    Complete Guide
                  </Link>
                  <Link
                    to="/guide/shoes"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-footprint-line text-sm"></i>
                    </span>
                    Shoes Guide
                  </Link>
                  <Link
                    to="/guide/clothing"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-t-shirt-line text-sm"></i>
                    </span>
                    Clothing Guide
                  </Link>
                  <Link
                    to="/guide/safety"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-shield-check-line text-sm"></i>
                    </span>
                    Safety Guide
                  </Link>
                  <Link
                    to="/guide/faq"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-question-answer-line text-sm"></i>
                    </span>
                    FAQ
                  </Link>
                </div>
              </div>

              {/* Category Links */}
              <div>
                <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3 px-2">
                  Product Categories
                </h4>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-background-100 hover:text-primary-500 transition-colors cursor-pointer"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-6 pt-5 border-t border-background-200/70">
                <p className="text-xs text-foreground-400 text-center">
                  &copy; 2026 Superbuy Sheet
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}